/**
 * View Base Module
 * Provides common infrastructure for all views
 */

import { emit, on, Events } from '../../../engine/core/events.js';

// ============================================
// View Registry
// ============================================

const views = new Map();
let currentView = null;

/**
 * Register a view renderer
 * @param {string} name - View name (bubble, mindmap, gantt, kanban, dashboard, routines, notes, markdown)
 * @param {Object} config - View configuration
 * @param {Function} config.render - Render function
 * @param {Function} [config.cleanup] - Cleanup function called when leaving view
 * @param {Function} [config.onEnter] - Called when entering view
 * @param {Function} [config.onLeave] - Called when leaving view
 */
export function registerView(name, config) {
    views.set(name, {
        render: config.render,
        cleanup: config.cleanup || (() => {}),
        onEnter: config.onEnter || (() => {}),
        onLeave: config.onLeave || (() => {}),
    });
}

/**
 * Get a registered view
 * @param {string} name - View name
 * @returns {Object|null} View config or null
 */
export function getView(name) {
    return views.get(name) || null;
}

/**
 * Get current view name
 * @returns {string|null}
 */
export function getCurrentView() {
    return currentView;
}

/**
 * Switch to a view
 * @param {string} name - View name
 * @param {Object} [context] - Optional context to pass to view
 */
export function switchView(name, context = {}) {
    const newView = views.get(name);
    if (!newView) {
        console.warn(`[Views] Unknown view: ${name}`);
        return false;
    }

    const previousView = currentView;

    // Leave current view
    if (currentView && views.has(currentView)) {
        const current = views.get(currentView);
        current.onLeave({ nextView: name });
        current.cleanup();
    }

    // Enter new view
    currentView = name;
    newView.onEnter({ previousView, ...context });

    emit(Events.VIEW_CHANGED, { view: name, previousView });

    return true;
}

/**
 * Render current view
 * @param {Object} [context] - Optional render context
 */
export function renderCurrentView(context = {}) {
    if (!currentView || !views.has(currentView)) {
        console.warn('[Views] No current view to render');
        return;
    }

    const view = views.get(currentView);
    view.render(context);
}

// ============================================
// View Container Management
// ============================================

const viewContainers = {
    bubble: 'bubble-view',
    mindmap: 'mindmap-view',
    gantt: 'gantt-view',
    routines: 'routines-view',
    kanban: 'kanban-view',
    dashboard: 'dashboard-view',
    notes: 'notes-view',
};

/**
 * Show a specific view container, hide others
 * @param {string} viewName - View to show
 */
export function showViewContainer(viewName) {
    Object.entries(viewContainers).forEach(([name, id]) => {
        const el = document.getElementById(id);
        if (el) {
            el.classList.toggle('active', name === viewName);
        }
    });
}

/**
 * Get the container element for a view
 * @param {string} viewName - View name
 * @returns {HTMLElement|null}
 */
export function getViewContainer(viewName) {
    const id = viewContainers[viewName];
    return id ? document.getElementById(id) : null;
}

// ============================================
// Tab Management
// ============================================

/**
 * Update tab active states
 * @param {string} activeView - Currently active view
 */
export function updateTabs(activeView) {
    document.querySelectorAll('.tab').forEach(tab => {
        tab.classList.toggle('active', tab.dataset.view === activeView);
    });
}

/**
 * Setup tab click handlers
 * @param {Function} onTabClick - Callback when tab is clicked
 */
export function setupTabs(onTabClick) {
    document.querySelectorAll('.tab').forEach(tab => {
        tab.addEventListener('click', () => {
            onTabClick(tab.dataset.view);
        });
    });
}

// ============================================
// View State Sync
// ============================================

/**
 * Sync view state to URL and UI
 * @param {string} view - Current view name
 */
export function syncViewState(view) {
    // Update tabs
    updateTabs(view);

    // Update URL if history API available
    if (window.history && window.history.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set('view', view);
        window.history.replaceState({}, '', url);
    }
}

/**
 * Get view from URL
 * @returns {string|null}
 */
export function getViewFromURL() {
    const params = new URLSearchParams(window.location.search);
    return params.get('view');
}

// ============================================
// Clear View Contents
// ============================================

/**
 * Clear all view contents for a fresh render
 */
export function clearAllViews() {
    // Clear bubble view
    const bubbleSvg = document.getElementById('bubble-svg');
    if (bubbleSvg) {
        const world = bubbleSvg.querySelector('.bubble-world');
        if (world) world.innerHTML = '';
    }

    // Clear mindmap view
    const mindmapWorld = document.querySelector('.mindmap-world');
    if (mindmapWorld) mindmapWorld.innerHTML = '';

    // Clear gantt view
    const ganttTimeline = document.querySelector('.gantt-timeline');
    if (ganttTimeline) ganttTimeline.innerHTML = '';

    // Clear kanban view
    ['kanban-todo', 'kanban-in-progress', 'kanban-done'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.innerHTML = '';
    });

    // Reset kanban counts
    ['kanban-count-todo', 'kanban-count-in-progress', 'kanban-count-done'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.textContent = '0';
    });

    // Clear dashboard
    const dashboardContent = document.getElementById('dashboard-content');
    if (dashboardContent) dashboardContent.innerHTML = '';

    // Clear notes view
    const notesFullView = document.getElementById('notes-full-view');
    if (notesFullView) notesFullView.innerHTML = '';
}
